<template>
  <main class="main">
    <div class="anim-wrap" :class="{ loading: loading }">
      <div class="container" v-if="selectedGroupData">
        <div class="row page-nav">
          <div class="col-md-8 hidden-sm">
            <nav class="breadcrumbs">
              <ol class="breadcrumb">
                <li>
                  <router-link to="/" class="link-item">Dashboard</router-link>
                </li>
                <li>
                  {{ selectedGroupData.groupName }}
                </li>
              </ol>
            </nav>
          </div>
          <div class="col-md-4 text-md-right">
            <router-link to="/" class="link-item"
              ><font-awesome-icon icon="angle-left" /> Back to
              Dashboard</router-link
            >
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <GroupPanel :groupData="selectedGroupData" :link="false" />
          </div>
        </div>
        <div class="row py-5">
          <div class="col-md-6 my-auto">
            <h3>
              Users
              <span v-if="selectedGroupData.users"
                >({{ selectedGroupData.users.length }})</span
              >
            </h3>
          </div>
          <div class="col-md-6 list-actions text-md-right">
            <button
              class="btn btn-round"
              :class="{ active: incognito }"
              @click="incognito = !incognito"
              title="Incognito mode"
              v-tippy="{ theme: 'navy' }"
            >
              <font-awesome-icon icon="user-secret" />
            </button>
            <b-dropdown>
              <template v-slot:button-content>
                Actions
              </template>
              <li>
                <span @click="promptInviteAll()" class="dropdown-item">
                  Send invites to new users
                </span>
              </li>
            </b-dropdown>
            <button @click="$toggleInviteModal()" class="btn btn-primary">
              <font-awesome-icon icon="plus" /> Add users
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div
              :id="user.id"
              v-bind:key="user.id"
              v-for="user in orderedUsers"
              class="panel panel-list panel-user"
            >
              <div
                v-if="getUserStatus(user) === 'completed'"
                class="icon icon-completed"
              >
                <font-awesome-icon icon="check" />
              </div>
              <div
                v-else-if="
                  getUserStatus(user) === 'partial' ||
                    getUserStatus(user) === 'progress'
                "
                class="icon icon-progress"
              >
                <font-awesome-icon :icon="['far', 'clock']" />
              </div>
              <div
                v-else-if="getUserStatus(user) === 'invited'"
                class="icon icon-invited"
              >
                <font-awesome-icon :icon="['far', 'paper-plane']" />
              </div>
              <div
                v-else-if="getUserStatus(user) === 'created'"
                class="icon icon-created"
              >
                <font-awesome-icon icon="user-circle" />
              </div>
              <div class="content">
                <h4>
                  {{ transformName(user) }}
                  <span v-if="!incognito">{{ user.email }}</span>
                </h4>
                <p
                  v-if="getUserStatus(user) === 'completed'"
                  class="status status-completed color-green"
                >
                  Completed assessments
                </p>
                <p
                  v-else-if="
                    getUserStatus(user) === 'partial' ||
                      getUserStatus(user) === 'progress'
                  "
                  class="status status-progress color-purple"
                >
                  In progress
                </p>
                <p
                  v-else-if="getUserStatus(user) === 'invited'"
                  class="status status-progress color-blue"
                >
                  Invited {{ timestampToTimeSince(user.invitationSent) }}
                </p>
                <p
                  v-else-if="getUserStatus(user) === 'created'"
                  class="status status-progress"
                >
                  User not invited
                </p>
              </div>
              <div class="panel-links">
                <router-link
                  v-if="user.completedInsights"
                  class="link active"
                  :to="'/results/insights/?id=' + user.id"
                  ><span>Insights</span>
                  <font-awesome-icon :icon="['far', 'check-circle']" />
                </router-link>
                <div v-else class="link">
                  <span>Insights</span>
                  <font-awesome-icon :icon="['far', 'circle']" />
                </div>
                <router-link
                  v-if="user.completedPathways"
                  class="link active"
                  :to="'/results/pathways/?id=' + user.id"
                  ><span>Pathways</span>
                  <font-awesome-icon :icon="['far', 'check-circle']" />
                </router-link>
                <div v-else class="link">
                  <span>Pathways</span>
                  <font-awesome-icon :icon="['far', 'circle']" />
                </div>
                <router-link
                  v-if="user.completedPathways || user.completedInsights"
                  class="link active"
                  :to="'/results/?id=' + user.id"
                  ><span>Combined</span>
                  <font-awesome-icon :icon="['far', 'newspaper']" />
                </router-link>
                <div v-else class="link">
                  <span>Combined</span>
                  <font-awesome-icon :icon="['far', 'newspaper']" />
                </div>
              </div>
              <div class="panel-actions">
                <b-dropdown right class="extra-actions" no-caret>
                  <template v-slot:button-content>
                    <font-awesome-icon icon="ellipsis-v" />
                  </template>
                  <li>
                    <router-link
                      :to="'/results/insights/?id=' + user.id"
                      class="dropdown-item"
                      :class="{ disabled: !user.completedInsights }"
                      >View Insights</router-link
                    >
                  </li>
                  <li>
                    <router-link
                      :to="'/results/pathways/?id=' + user.id"
                      class="dropdown-item"
                      :class="{ disabled: !user.completedPathways }"
                      >View Pathways</router-link
                    >
                  </li>
                  <router-link
                    :to="'/hub/?code=' + user.id"
                    class="dropdown-item"
                    >Open Hub</router-link
                  >
                  <b-dropdown-divider></b-dropdown-divider>
                  <li>
                    <span
                      @click="inviteUsers([{ id: user.id, email: user.email }])"
                      class="dropdown-item"
                    >
                      <span v-if="user.invitationSent">Re-send invite</span>
                      <span v-else>Send invite</span>
                    </span>
                  </li>
                  <li>
                    <span
                      class="dropdown-item color-red"
                      @click="promptDeleteUser(user.email, user.id)"
                      >Delete user</span
                    >
                  </li>
                </b-dropdown>
              </div>
            </div>
            <div
              v-if="!orderedUsers.length"
              class="panel panel-flat text-center"
            >
              <h1>No users added to the group</h1>
              <p>
                Add users to the group to get started.
                <a href="#" @click="$toggleInviteModal()"
                  >Click here to add users</a
                >.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <InviteModal :groupId="id" />
  </main>
</template>

<script>
// FireBase
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/functions";
import { mapState } from "vuex";
import InviteModal from "@/components/InviteModal.vue";
import GroupPanel from "@/components/GroupPanel.vue";

var _ = require("lodash");

export default {
  name: "dashboard",
  components: { InviteModal, GroupPanel },
  data() {
    return {
      id: null,
      loading: false,
      incognito: false,
    };
  },
  computed: {
    ...mapState(["user"]),
    ...mapState(["userData"]),
    ...mapState(["organisationData"]),
    ...mapState(["groupsData"]),
    getUser() {
      return this.user ? this.user : "";
    },
    getGroupsData() {
      return this.groupsData ? this.groupsData : "";
    },
    getOrganisationData() {
      return this.organisationData ? this.organisationData : "";
    },
    getUserData() {
      return this.userData ? this.userData : "";
    },
    selectedGroupData() {
      if (this.getGroupsData) {
        return this.getGroupsData.find((o) => o.id === this.id);
      } else {
        return false;
      }
    },
    orderedUsers: function() {
      return _.orderBy(
        this.selectedGroupData.users,
        "createDate.seconds"
      ).reverse();
    },
  },
  mounted() {
    this.id = this.$route.query.group;
  },
  methods: {
    promptInviteAll() {
      let users = [];
      if (this.selectedGroupData) {
        if (this.selectedGroupData.users) {
          this.selectedGroupData.users.forEach((user) => {
            if (user.invitationSent === false) {
              let userDoc = { id: user.id, email: user.email };
              users.push(userDoc);
            }
          });
        }
      }
      if (users.length === 0) {
        this.$toast.open({
          message: "All users have been invited already",
          type: "error",
        });
        return true;
      }
      let options = {
        html: true,
        okText: "Invite users",
        cancelText: "Cancel",
      };
      let message =
        "You are about to send invites to " +
        users.length +
        " users. Are you sure you want to send invites?";
      this.$dialog
        .confirm(message, options)
        .then(() => {
          this.inviteUsers(users);
        })
        .catch(() => {});
    },
    inviteUsers(users) {
      if (users.length) {
        this.loading = true;
        let inviteUsers = firebase.functions().httpsCallable("inviteUsers");

        inviteUsers({
          groupId: this.selectedGroupData.id,
          organisationName: this.getOrganisationData.name,
          users: users,
        })
          .then((result) => {
            this.loading = false;
            if (result.data) {
              let count = result.data;
              this.$toast.open({
                message: count + " user(s) invited",
                type: "success",
              });
            }
          })
          .catch((error) => {
            this.loading = false;
            console.log(error);
          });
      }
    },
    addUsers() {
      this.loading = true;
      let addUsers = firebase.functions().httpsCallable("addUsers");

      if (this.add.emailAddress !== "") {
        let row = {
          email: this.add.emailAddress,
          firstName: this.add.firstName,
          lastName: this.add.lastName,
        };
        this.add.emailAddress = "";
        this.add.firstName = "";
        this.add.lastName = "";
        this.emailList.push(row);
      }

      addUsers({
        groupId: this.selectedGroupData.id,
        users: this.emailList,
      })
        .then((result) => {
          this.loading = false;
          this.emailList = [];
          if (result.data) {
            let count = result.data.length;
            this.$toast.open({
              message: count + " user(s) added to the group",
              type: "success",
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    promptDeleteUser(email, id) {
      let options = {
        html: true,
        okText: "Delete user",
        cancelText: "Cancel",
      };
      let message =
        "You are about to delete the user <b>" +
        email +
        "</b> including all the userdata and testdata tied to this user. All the information will be lost and this action can not be reversed. Are you sure you want to delete this user?";
      this.$dialog
        .confirm(message, options)
        .then(() => {
          this.deleteUser(id);
        })
        .catch(() => {});
    },
    deleteUser(id) {
      document.getElementById(id).classList.add("disabled");
      let deleteUser = firebase.functions().httpsCallable("deleteUser");

      deleteUser({
        docId: id,
      })
        .then(() => {
          this.$toast.open({
            message: "User removed succesfully",
            type: "success",
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    timestampToTimeSince(timestamp) {
      let time = this.$timeSince(new Date(timestamp.seconds * 1000));
      return time;
    },
    transformName(user) {
      if (this.incognito || user.firstName === "") {
        let truncated = user.id.toUpperCase();
        let name = "User " + truncated.substring(0, 5);
        return name;
      } else {
        return user.firstName + " " + user.lastName;
      }
    },
    getUserStatus(user) {
      let completedAssessments = 0;
      let activeAccessments = 0;

      if (this.selectedGroupData.includeInsights) {
        activeAccessments++;
        if (user.completedInsights) {
          completedAssessments++;
        }
      }

      if (this.selectedGroupData.includePathways) {
        activeAccessments++;
        if (user.completedPathways) {
          completedAssessments++;
        }
      }

      if (
        completedAssessments !== 0 &&
        completedAssessments !== activeAccessments
      ) {
        return "partial";
      } else if (completedAssessments === activeAccessments) {
        return "completed";
      }

      if (user.hasLoggedIn) {
        return "progress";
      }

      if (user.invitationSent) {
        return "invited";
      } else {
        return "created";
      }
    },
  },
};
</script>
